import React from 'react'
import { Box, TextField, Button, Chip, useTheme, useMediaQuery } from '@mui/material'
import { CollapsibleInputCard } from '../../../components/_WMS/CollapsibleInputCard'
import { toDunPacks } from '../utils/dunUtils'

interface QuantitySectionProps {
  quantityInputRef: React.RefObject<HTMLInputElement>
  localQuantity: string
  handleQuantityChange: (value: string) => void
  quantityValidated: boolean
  error?: string
  handleValidateQuantity: () => void
  handleEditQuantity: () => void
  sharedState: {
    cantidad: number | null
    selectedBatch?: { quantity?: number }
    availableStock?: number
    isDun?: boolean
    dunCode?: string
    dunQty?: number
    typePackageName?: string
    cantidadDun?: number | null
  }
}

export const QuantitySection: React.FC<QuantitySectionProps> = ({
  quantityInputRef,
  localQuantity,
  handleQuantityChange,
  quantityValidated,
  error,
  handleValidateQuantity,
  handleEditQuantity,
  sharedState
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const isDun = Boolean(sharedState.isDun)
  // Nombre de la unidad de medida del DUN (Pack, Caja, Pallet) para etiquetar el input.
  const packLabel = sharedState.typePackageName || 'Piezas agrupadas'

  const maxUnits = sharedState.selectedBatch
    ? (sharedState.selectedBatch.quantity || 0)
    : (sharedState.availableStock || 0)

  // En modo DUN el operador ingresa cajas completas, no unidades.
  const maxQuantity = isDun ? toDunPacks(maxUnits, sharedState.dunQty) : maxUnits

  const hasQuantityError = Boolean(localQuantity && parseInt(localQuantity) > maxQuantity)

  const getQuantityLabel = () => {
    const field = isDun ? `Cantidad de ${packLabel}` : 'Cantidad'
    if (sharedState.selectedBatch) {
      const batchInfo = sharedState.selectedBatch
      return `${field} (Lote: ${(batchInfo as any).batchNumber || 'N/A'} - Máx: ${maxQuantity})`
    }
    return `${field} (Máx: ${maxQuantity})`
  }

  const collapsedSummary = isDun
    ? `Cantidad: ${sharedState.cantidadDun} ${packLabel} (${sharedState.cantidad} unidades)`
    : `Cantidad: ${sharedState.cantidad} unidades`

  // El DUN se muestra con los ultimos cuatro digitos del dun para evitar
  // posibles reescaneos, pero no perdiendo la trazabilidad del dato.
  const dunChip =
    isDun && sharedState.dunCode ? (
      <Chip
        label={`DUN ···${sharedState.dunCode.slice(-4)}`}
        title={sharedState.dunCode}
        aria-label={`DUN ${sharedState.dunCode}`}
        variant='outlined'
        size='small'
      />
    ) : null

  return (
    <Box mb={1}>
      <CollapsibleInputCard
        title={isDun ? `Cantidad a Reubicar (${packLabel})` : 'Cantidad a Reubicar'}
        isValidated={quantityValidated}
        validationFailed={hasQuantityError}
        onEdit={handleEditQuantity}
        isMobile={isMobile}
        expandedContent={
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'}>
            <TextField
              inputRef={quantityInputRef}
              label={getQuantityLabel()}
              type="number"
              variant="outlined"
              fullWidth
              value={localQuantity}
              onChange={(e) => handleQuantityChange(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleValidateQuantity()
                }
              }}
              placeholder={`Máximo: ${maxQuantity}`}
              inputProps={{
                min: 1,
                max: maxQuantity
              }}
              error={hasQuantityError || Boolean(error)}
              helperText={
                error ||
                (hasQuantityError
                  ? `La cantidad no puede ser mayor a ${maxQuantity}${isDun ? ` ${packLabel}` : ''}`
                  : '')
              }
              style={{
                marginRight: isMobile ? 0 : theme.spacing(1),
                marginBottom: isMobile ? theme.spacing(2) : 0
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleValidateQuantity}
              disabled={!localQuantity.trim() || parseInt(localQuantity) <= 0}
              fullWidth={isMobile}
              style={{ minWidth: isMobile ? 'auto' : '100px' }}
            >
              Validar
            </Button>
          </Box>
        }
        collapsedSummary={collapsedSummary}
        collapsedChips={dunChip}
      />
    </Box>
  )
}